import React, { useState, useEffect } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Tag, 
    Button, 
    Space, 
    Select, 
    message, 
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    PartitionOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Content } = Layout;
const { Title } = Typography;

const InventoryReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [types, setTypes] = useState([]);
    const [filters, setFilters] = useState({
        category: '',
        type: '',
        status: ''
    });

    // Fetch Master Data
    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                const [catRes, typeRes] = await Promise.all([
                    axiosClient.get('/master/category/get_all_categories_dropdown.php'),
                    axiosClient.get('/master/type/get_all_types_dropdown.php')
                ]);
                
                if (catRes.data.status === 'success') setCategories(catRes.data.data);
                if (typeRes.data.status === 'success') setTypes(typeRes.data.data);
            } catch (error) {
                console.error('Master data fetch error:', error);
                message.error('Failed to load filter options.');
            }
        };
        fetchMasterData();
    }, []);

    const columns = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            width: 120,
        },
        {
            title: 'Item Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: 'Item Type',
            dataIndex: 'type',
            key: 'type',
            width: 130,
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 130,
        },
        {
            title: 'Item Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => (
                <Tag color={status == 1 ? 'green' : 'red'}>
                    {status == 1 ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Register Date',
            dataIndex: 'register_date',
            key: 'register_date',
            width: 150,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        },
        {
            title: 'End of Life Date',
            dataIndex: 'eol_date',
            key: 'eol_date',
            width: 150,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY') : 'N/A'
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
            ellipsis: true,
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.category) params.append('category', filters.category);
            if (filters.type) params.append('type', filters.type);
            if (filters.status) params.append('status', filters.status);

            const response = await axiosClient.get(`/reports/get_inventory_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No records found for the selected filters.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch report data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            category: '',
            type: '',
            status: ''
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Item Code': item.item_code,
            'Item Name': item.name,
            'Item Type': item.type,
            'Item Category': item.category,
            'Item Status': item.status == 1 ? 'Active' : 'Inactive',
            'Register Date': item.register_date,
            'End of Life Date': item.eol_date,
            'Note': item.note
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Inventory_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <PartitionOutlined />
                            <span>Inventory Report</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Item Category</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Category"
                                value={filters.category}
                                onChange={(val) => setFilters({...filters, category: val})}
                            >
                                <Select.Option value="">All Categories</Select.Option>
                                {categories.map(cat => (
                                    <Select.Option key={cat.id} value={cat.value}>{cat.value}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Item Type</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Type"
                                value={filters.type}
                                onChange={(val) => setFilters({...filters, type: val})}
                            >
                                <Select.Option value="">All Types</Select.Option>
                                {types.map(t => (
                                    <Select.Option key={t.id} value={t.value}>{t.value}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500 }}>Item Status</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select Status"
                                value={filters.status}
                                onChange={(val) => setFilters({...filters, status: val})}
                            >
                                <Select.Option value="">All Status</Select.Option>
                                <Select.Option value="1">Active</Select.Option>
                                <Select.Option value="0">Inactive</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} records)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                        scroll={{ x: 1200 }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default InventoryReport;
